import { useMemo } from "react";
import CustomSelect from "../../components/Select/CustomSelect";
import { ICON_SIZES, TRANSACTION_STATUSES } from "../../helpers/constants";
import useCurrentUser from "../../hooks/useCurrentUser";
import { transformToSelectOptions } from "../../helpers/utils";
import clsx from "clsx";
import TextInput from "../../components/TextInput/TextInput";
import CollapsibleBlock from "../../components/CollapsibleBlock/CollapsibleBlock";
import { useTranslation } from "react-i18next";
import useMobile from "../../hooks/useMobile";
import useCollapsibles, {
  ETransactionPageMobileCollapsibles,
} from "../../hooks/useCollapsibles";
import { MdDoneAll, MdOutlinePriorityHigh } from "react-icons/md";
// import { TRANSACTION_STATUS_PENDING } from "src/constants/transaction-status";

interface ITransactionPayment {
  onMethodChange: (value: string) => void;
  method: string;
  onStatusChange: (value: string) => void;
  status: string;
  receiptNumber: string;
  onReceiptNumberChange: (value: string) => void;
}

const TransactionPayment: React.FC<ITransactionPayment> = ({
  method,
  onMethodChange,
  status,
  onStatusChange,
  receiptNumber,
  onReceiptNumberChange,
}) => {
  const { allEnabledPaymentMethods, getPaymentMethodById } = useCurrentUser();
  const { t } = useTranslation();
  const {
    isTransactionPageMobileCollapsibleOpen,
    collapsiblePreferences,
    toggleTransactionPageMobileCollapsible,
    updateCollapsible,
  } = useCollapsibles();
  const { isMobile } = useMobile();
  const paymentMethods = useMemo(() => {
    return transformToSelectOptions({
      data: allEnabledPaymentMethods,
      labelField: "name",
      valueField: "id",
    });
  }, [allEnabledPaymentMethods]);

  const transactionStatuses = useMemo(() => {
    return transformToSelectOptions({
      data: TRANSACTION_STATUSES(),
      labelField: "name",
      valueField: "id",
    });
  }, []);

  const paymentDataIsValid = useMemo(() => {
    return !!method && !!status;
  }, [method, status]);

  return (
    <CollapsibleBlock
      title={t("transaction_page.collapsibles.payment.title")}
      icon={
        paymentDataIsValid ? (
          <MdDoneAll size={ICON_SIZES.SM} className="text-lime-400" />
        ) : (
          <MdOutlinePriorityHigh
            size={ICON_SIZES.SM}
            className="text-red-500 dark:text-rose-400/80"
          />
        )
      }
      uppercase
      open={
        isMobile
          ? isTransactionPageMobileCollapsibleOpen(
              ETransactionPageMobileCollapsibles.PAYMENT
            )
          : collapsiblePreferences.transactionPaymentOpen
      }
      onChange={(isOpen: boolean) => {
        if (isMobile) {
          toggleTransactionPageMobileCollapsible(
            ETransactionPageMobileCollapsibles.PAYMENT
          );
        } else {
          updateCollapsible({
            transactionPaymentOpen: isOpen,
          });
        }
      }}
      badge={
        paymentDataIsValid
          ? getPaymentMethodById(method)?.name || "Desconocido"
          : t("fields_validation.incomplete.badge")
      }
      badgeKind={paymentDataIsValid ? "info" : "danger"}
    >
      <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
        <CustomSelect
          options={paymentMethods}
          onChange={(e: any) => onMethodChange(e?.value)}
          label={t("transaction_page.collapsibles.payment.payment_method")}
          value={method}
          placeholder={t(
            "transaction_page.collapsibles.payment.payment_method_placeholder"
          )}
          useValueAsLabel={false}
          required
          isSearchable={false}
        />
        <CustomSelect
          options={transactionStatuses}
          onChange={(e: any) => onStatusChange(e?.value)}
          label={t("transaction_page.collapsibles.payment.status")}
          value={status}
          placeholder={t(
            "transaction_page.collapsibles.payment.status_placeholder"
          )}
          useValueAsLabel={false}
          required
          isSearchable={false}
        />
        {/* {status === TRANSACTION_STATUS_PENDING && (
          <TextInput
            type="date"
            label="Fecha de vencimiento"
            onChange={() => {}}
            value=""
            required
            placeholder="Selecciona una fecha de vencimiento"
          />
        )} */}
        <TextInput
          onChange={(e: any) => onReceiptNumberChange(e?.target?.value)}
          label={t("transaction_page.collapsibles.payment.voucher")}
          value={receiptNumber}
          placeholder={t(
            "transaction_page.collapsibles.payment.voucher_placeholder"
          )}
        />
      </div>
    </CollapsibleBlock>
  );
};

export default TransactionPayment;
